import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  ArrowRight, Sparkles, MessageCircle, Calendar, TrendingUp,
  Users, Wallet, FileText, Bell, Globe, Smartphone, Check, PlayCircle, Heart,
} from "lucide-react";
import PondokIcon from "../components/PondokIcon";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

const OWNER_WA = "6282397614466";
const WA_PREFILL = encodeURIComponent(
  "Halo, saya tertarik menggunakan PondokRaja untuk homestay saya. Bisa info lebih lanjut?"
);
const WA_URL = `https://wa.me/${OWNER_WA}?text=${WA_PREFILL}`;

const FEATURES = [
  {
    icon: Calendar,
    title: "Booking & Kalender",
    desc: "Kelola reservasi dari Booking.com, Airbnb, dan direct booking dalam 1 kalender. Anti-konflik otomatis.",
  },
  {
    icon: MessageCircle,
    title: "WhatsApp Otomatis",
    desc: "Konfirmasi booking, reminder pembayaran H-3, dan check-in H-1 dikirim otomatis via Fonnte. Bilingual ID/EN.",
  },
  {
    icon: TrendingUp,
    title: "Dashboard & Reports",
    desc: "KPI harian, grafik profit 3 bulan, laporan bulanan/tahunan/custom range. Export PDF & share ke WA.",
  },
  {
    icon: Wallet,
    title: "Expense Tracking",
    desc: "Catat gaji staff, listrik, maintenance, supplies. Profit bersih otomatis dihitung (Revenue − Expenses).",
  },
  {
    icon: Users,
    title: "Database Tamu (CRM)",
    desc: "Riwayat tamu lengkap, tier loyalitas otomatis (Baru/Kembali/Langganan), kirim salam ulang via WA.",
  },
  {
    icon: FileText,
    title: "Invoice & iCal Import",
    desc: "Invoice PDF print-ready dalam Indonesia atau English. Import iCal dari Booking.com untuk sync otomatis.",
  },
  {
    icon: Bell,
    title: "Reminder H-1 Manual",
    desc: "Buka halaman reminder, tap 'Kirim WA' per tamu yang check-in besok. Gratis tanpa biaya API.",
  },
  {
    icon: Smartphone,
    title: "Installable di HP",
    desc: "PWA — install di home-screen HP staff front desk seperti aplikasi native. Mobile-responsive penuh.",
  },
];

const WHY = [
  {
    icon: Heart,
    title: "Dibuat untuk homestay kecil Indonesia",
    desc: "Bukan template luar negeri. Bahasa Indonesia, format Rupiah, platform Booking.com/Airbnb/StayRajaAmpat, nomor WA +62 — semua natively.",
  },
  {
    icon: Sparkles,
    title: "Simple tapi Powerful",
    desc: "Tidak ada tombol yang sia-sia. Setiap fitur dirancang untuk menghemat waktu owner yang sudah sibuk operasional.",
  },
  {
    icon: Globe,
    title: "Zero-cost untuk mulai",
    desc: "Semua fitur gratis. Hanya WhatsApp auto yang opsional bayar Rp 50rb/bulan ke Fonnte — itupun kalau mau, bisa dilewati.",
  },
];

export default function Landing() {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [demoLoading, setDemoLoading] = useState(false);

  const tryDemo = async () => {
    setDemoLoading(true);
    try {
      const res = await api.post("/demo/login");
      setSession(res.data.access_token, res.data.user);
      toast.success("Selamat datang di Demo PondokRaja 🌴");
      navigate("/dashboard");
    } catch (e) {
      toast.error("Gagal memuat demo. Coba lagi.");
      setDemoLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#172E21] overflow-x-hidden">
      {/* Nav */}
      <nav className="sticky top-0 z-40 border-b border-[#EBE6DE] bg-[#FDFBF7]/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-10 w-10 rounded-2xl bg-[#D16B4D] flex items-center justify-center shadow-sm">
              <PondokIcon className="h-5 w-5 text-white" strokeWidth={1.8} />
            </div>
            <div>
              <div className="font-heading text-lg font-semibold leading-none">PondokRaja</div>
              <div className="text-[10px] text-[#586D61] uppercase tracking-widest mt-0.5">Booking Manager</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="nav-login-link">
              <Button variant="ghost" className="rounded-xl text-sm">Login</Button>
            </Link>
            <a href={WA_URL} target="_blank" rel="noreferrer">
              <Button
                data-testid="btn-nav-contact-wa"
                className="bg-[#25D366] hover:bg-[#1FA855] text-white rounded-xl text-sm gap-1.5"
              >
                <MessageCircle className="h-4 w-4" />
                <span className="hidden sm:inline">Hubungi</span>
              </Button>
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-12 lg:pt-20 pb-16">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-center">
          <div className="lg:col-span-7 space-y-7">
            <div
              data-testid="early-adopter-badge"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#D16B4D]/10 border border-[#D16B4D]/30 text-[#D16B4D] text-xs font-bold uppercase tracking-wider"
            >
              <Sparkles className="h-3.5 w-3.5" />
              Gratis untuk 10 homestay pertama
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.1] tracking-tight">
              Kelola tamu, kamar, dan laporan —{" "}
              <span className="text-[#D16B4D]">semudah menyapa.</span>
            </h1>
            <p className="text-base sm:text-lg text-[#586D61] max-w-2xl leading-relaxed">
              Booking manager yang dibuat untuk homestay kecil Indonesia. Kelola reservasi multi-platform,
              kirim WhatsApp otomatis ke tamu, dan pantau profit dalam satu aplikasi ringan yang bisa
              di-install di HP front desk Anda.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                data-testid="btn-hero-demo"
                onClick={tryDemo}
                disabled={demoLoading}
                size="lg"
                className="bg-[#D16B4D] hover:bg-[#B8553B] text-white rounded-xl text-base gap-2 h-12 px-6"
              >
                <PlayCircle className="h-5 w-5" />
                {demoLoading ? "Memuat demo…" : "Coba Demo Gratis"}
                <ArrowRight className="h-4 w-4" />
              </Button>
              <a href={WA_URL} target="_blank" rel="noreferrer">
                <Button
                  data-testid="btn-hero-contact"
                  size="lg"
                  variant="outline"
                  className="rounded-xl text-base gap-2 h-12 px-6 border-[#172E21] hover:bg-[#172E21] hover:text-[#FDFBF7]"
                >
                  <MessageCircle className="h-5 w-5" />
                  Tanya via WhatsApp
                </Button>
              </a>
            </div>
            <div className="flex flex-wrap items-center gap-5 text-sm text-[#586D61] pt-2">
              <div className="flex items-center gap-1.5">
                <Check className="h-4 w-4 text-[#2C7A51]" />
                Tanpa kartu kredit
              </div>
              <div className="flex items-center gap-1.5">
                <Check className="h-4 w-4 text-[#2C7A51]" />
                Data demo real
              </div>
              <div className="flex items-center gap-1.5">
                <Check className="h-4 w-4 text-[#2C7A51]" />
                Bahasa Indonesia
              </div>
            </div>
          </div>

          {/* Hero illustration — stylized dashboard mock */}
          <div className="lg:col-span-5">
            <div className="relative">
              {/* Background palmtree ornament */}
              <div className="absolute -top-8 -right-8 h-40 w-40 rounded-full bg-[#D16B4D]/10 blur-3xl" />
              <div className="absolute -bottom-8 -left-8 h-32 w-32 rounded-full bg-[#2C7A51]/15 blur-3xl" />

              <div className="relative bg-white rounded-3xl shadow-xl border border-[#EBE6DE] p-6 rotate-1 hover:rotate-0 transition-transform">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-[#586D61]">Februari 2026</div>
                    <div className="font-heading text-lg font-medium">Dashboard</div>
                  </div>
                  <div className="h-8 w-8 rounded-xl bg-[#D16B4D]/15 flex items-center justify-center">
                    <PondokIcon className="h-4 w-4 text-[#D16B4D]" strokeWidth={1.8} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2.5 mb-4">
                  <MiniKpi label="Pendapatan" value="Rp 8.1jt" accent="#D16B4D" />
                  <MiniKpi label="Profit" value="Rp 7.3jt" accent="#2C7A51" />
                  <MiniKpi label="Booking" value="2" accent="#082618" />
                  <MiniKpi label="Okupansi" value="68%" accent="#D99338" />
                </div>
                <div className="rounded-xl bg-gradient-to-br from-[#FDFBF7] to-[#EBE6DE]/50 p-4 border border-[#EBE6DE]">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-[#586D61] mb-2">Tren Profit</div>
                  <svg viewBox="0 0 200 50" className="w-full h-12">
                    <polyline
                      points="10,40 55,30 100,32 145,18 190,8"
                      fill="none" stroke="#2C7A51" strokeWidth="2.5" strokeLinecap="round"
                    />
                    <circle cx="190" cy="8" r="4" fill="#2C7A51" stroke="white" strokeWidth="2" />
                  </svg>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[10px] text-[#586D61]">Des Jan Feb</span>
                    <span className="text-[10px] font-bold text-[#2C7A51]">+180%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-24">
        <div className="text-center mb-12 max-w-2xl mx-auto">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#D16B4D] mb-3">Fitur Lengkap</div>
          <h2 className="font-heading text-3xl lg:text-4xl font-medium leading-tight">
            Semua yang Anda butuhkan, dalam satu aplikasi ringan.
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {FEATURES.map((f, i) => (
            <div
              key={f.title}
              data-testid={`feature-card-${i}`}
              className="bg-white rounded-2xl border border-[#EBE6DE] p-6 hover:border-[#D16B4D]/40 hover:shadow-md transition-all group"
            >
              <div className="h-11 w-11 rounded-xl bg-[#D16B4D]/10 flex items-center justify-center text-[#D16B4D] mb-4 group-hover:bg-[#D16B4D] group-hover:text-white transition-colors">
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="font-heading text-lg font-semibold mb-2">{f.title}</h3>
              <p className="text-sm text-[#586D61] leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Why PondokRaja */}
      <section className="bg-[#172E21] text-[#FDFBF7] py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center mb-12 max-w-2xl mx-auto">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#D16B4D] mb-3">Kenapa PondokRaja</div>
            <h2 className="font-heading text-3xl lg:text-4xl font-medium leading-tight">
              Bukan software hotel besar yang rumit.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {WHY.map((w, i) => (
              <div
                key={w.title}
                data-testid={`why-card-${i}`}
                className="rounded-2xl border border-[#FDFBF7]/10 p-6 bg-[#FDFBF7]/5 backdrop-blur"
              >
                <div className="h-11 w-11 rounded-xl bg-[#D16B4D] flex items-center justify-center text-white mb-4">
                  <w.icon className="h-5 w-5" />
                </div>
                <h3 className="font-heading text-lg font-semibold mb-2">{w.title}</h3>
                <p className="text-sm text-[#FDFBF7]/70 leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-4xl mx-auto px-5 lg:px-8 py-16 lg:py-24 text-center">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#D16B4D] mb-3">Mulai Sekarang</div>
        <h2 className="font-heading text-3xl lg:text-5xl font-medium leading-tight mb-5">
          Siap menyederhanakan operasional homestay Anda?
        </h2>
        <p className="text-base text-[#586D61] mb-8 max-w-xl mx-auto">
          Coba demo dengan data contoh lengkap — 8 booking, 3 kamar, laporan keuangan.
          Atau langsung tanya saya via WhatsApp tentang cara pakai untuk homestay Anda.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Button
            data-testid="btn-cta-demo"
            onClick={tryDemo}
            disabled={demoLoading}
            size="lg"
            className="bg-[#D16B4D] hover:bg-[#B8553B] text-white rounded-xl text-base gap-2 h-12 px-6"
          >
            <PlayCircle className="h-5 w-5" />
            {demoLoading ? "Memuat…" : "Coba Demo Gratis"}
          </Button>
          <a href={WA_URL} target="_blank" rel="noreferrer">
            <Button
              data-testid="btn-cta-contact"
              size="lg"
              variant="outline"
              className="rounded-xl text-base gap-2 h-12 px-6 border-[#25D366] text-[#1FA855] hover:bg-[#25D366] hover:text-white"
            >
              <MessageCircle className="h-5 w-5" />
              Hubungi via WhatsApp
            </Button>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#EBE6DE] py-8 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-[#586D61]">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-xl bg-[#D16B4D] flex items-center justify-center">
              <PondokIcon className="h-4 w-4 text-white" strokeWidth={1.8} />
            </div>
            <div>
              <div className="font-heading text-sm font-semibold text-[#172E21]">PondokRaja</div>
              <div className="text-[10px] text-[#586D61] uppercase tracking-widest">Booking Manager</div>
            </div>
          </div>
          <div className="text-xs">
            Dibuat dengan 🌴 untuk homestay Indonesia
          </div>
          <a
            href={WA_URL}
            target="_blank" rel="noreferrer"
            data-testid="footer-contact-link"
            className="text-xs text-[#D16B4D] hover:underline"
          >
            Hubungi admin
          </a>
        </div>
      </footer>
    </div>
  );
}

function MiniKpi({ label, value, accent }) {
  return (
    <div className="rounded-xl bg-[#FDFBF7] p-2.5 border border-[#EBE6DE]">
      <div className="text-[9px] font-bold uppercase tracking-widest text-[#586D61]">{label}</div>
      <div className="font-mono text-base font-semibold mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}
